import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  AppBar,
  ButtonGroup,
  ClickAwayListener,
  Container,
  Grow,
  IconButton,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Stack,
  Tab,
  Tabs,
  Toolbar,
  Typography,
} from '@mui/material';
import Button from '@mui/material/Button'
import { useEditor } from '@craftjs/core';
import lz from 'lzutf8';
import { EditorContext, Props, ViewPort } from '../Editor';
import { useNavigate, useParams } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { ArrowDown01Icon, ArrowTurnBackwardIcon, ArrowTurnForwardIcon, ComputerIcon, SmartPhone01Icon, Tablet01Icon } from "@hugeicons/core-free-icons";

const Header = ({
  title,
  actions: secondaryActions = [],
  template,
  onSave,
  onClose,
}: Props) => {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const { actions, query } = useEditor();
  const [open, setOpen] = useState(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { isLoading, setIsLoading, viewPort, setViewPort } = useContext(EditorContext);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleCloseActions = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  const handleTemplateBackups = () => {
    if (!templateId) return;

    interface Backup {
      templateId: string;
      data: string;
      styles: string;
      lastUpdatedAt: number;
    }

    const templateBackups: Record<string, Backup[]> = JSON.parse(
      localStorage.getItem('templateBackups') ?? '{}'
    );
    const unsortedBackups: Backup[] = templateBackups[templateId] ?? [];

    const sortedBackups: Backup[] = unsortedBackups.sort((a, b) => {
      if (a.lastUpdatedAt < b.lastUpdatedAt) {
        return -1;
      } else if (a.lastUpdatedAt > b.lastUpdatedAt) {
        return 1;
      }
      return 0;
    });

    const newData = query.serialize();
    const compressedData = lz.encodeBase64(lz.compress(newData));

    if (sortedBackups.length > 18) {
      sortedBackups.shift();
    }

    sortedBackups.push({
      templateId: templateId,
      data: compressedData,
      styles: template.styles,
      lastUpdatedAt: Date.now(),
    });

    localStorage.setItem(
      'templateBackups',
      JSON.stringify({
        ...templateBackups,
        [templateId]: sortedBackups,
      })
    );
  };

  const updateViewportUrl = (viewport: string) => {
    navigate(document.location.pathname + "?" + viewport);
  };

  const handleViewport = (_: string, value: string) => {
    const qs = new URLSearchParams(document.location.search);
    qs.set("viewport", value);
    updateViewportUrl(qs.toString());
  };

  useEffect(() => {
    const qs = new URLSearchParams(document.location.search);
    const vp = qs.get("viewport");

    if (vp) {
      const isStandardViewport = ["sm", "md", "lg"].includes(vp);

      if (isStandardViewport) {
        setViewPort(vp as ViewPort);
      } else {
        qs.set("viewport", "sm");
        updateViewportUrl(qs.toString());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document.location.search]);

  useEffect(() => {
    const saveDraftInterval = setInterval(
      () => {
        handleTemplateBackups();
      },
      1000 * 60 * 5
    ); // 5 min

    return () => {
      clearInterval(saveDraftInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mainActions = useMemo(
    () => [
      {
        children: 'Guardar',
        onClick: async () => {
          setIsLoading(true);
          const newData = query.serialize();
          const compressedData = lz.encodeBase64(lz.compress(newData));
          await onSave(compressedData);
          setIsLoading(false);
        },
        loading: isLoading
      },
      ...secondaryActions,
    ],
    [onSave, query, secondaryActions, isLoading]
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleMenuItemClick = (e: any, index: number) => {
    const selectedMenu = mainActions[index];

    setSelectedIndex(index);
    selectedMenu.onClick && selectedMenu.onClick(e);
    setOpen(false);
  };

  return (
    <AppBar position="static" color="default" elevation={1}>
      <Container maxWidth={false}>
        <Toolbar disableGutters>
          <Stack spacing={2} direction="row" alignItems="center" flexGrow={1}>
            <img
              width={38}
              height={30}
              src="https://firebasestorage.googleapis.com/v0/b/menuapp-b44a8.appspot.com/o/assets%2Fbl-logo.png?alt=media&token=351ba0f0-26c4-434d-922d-5c3f6ad9d245"
            />
            <Typography variant="h4" noWrap>
              {title ?? ''}
            </Typography>

            <Stack spacing={0} direction="row" alignItems="center" flexGrow={1}>
              <IconButton
                size="large"
                color="primary"
                onClick={() => actions.history.undo()}
              >
                <HugeiconsIcon icon={ArrowTurnBackwardIcon} />
              </IconButton>
              <IconButton
                size="large"
                color="primary"
                onClick={() => actions.history.redo()}
              >
                <HugeiconsIcon icon={ArrowTurnForwardIcon} />
              </IconButton>
            </Stack>
          </Stack>

          <Stack
            spacing={1}
            direction="row"
            alignItems="center"
            justifyContent="center"
            flexGrow={2}
          >
            <Stack spacing={2} direction={"row"} justifyContent="center">
              <Tabs value={viewPort} onChange={handleViewport} indicatorColor="primary">
                <Tab value="sm" icon={<HugeiconsIcon icon={SmartPhone01Icon} />} />
                <Tab value="md" icon={<HugeiconsIcon icon={Tablet01Icon} />} />
                <Tab value="lg" icon={<HugeiconsIcon icon={ComputerIcon} />} />
              </Tabs>
            </Stack>
          </Stack>

          <Stack spacing={1} direction="row" flexGrow={1} justifyContent="end">
            <Button variant="text" onClick={onClose}>
              Cancelar
            </Button>
            <ButtonGroup
              size="medium"
              variant="contained"
              disableElevation
              color="success"
              ref={anchorRef}
            >
              <Button {...mainActions[selectedIndex]} />
              <Button size="small" onClick={handleToggle}>
                <HugeiconsIcon icon={ArrowDown01Icon} />
              </Button>
            </ButtonGroup>
            <Popper
              sx={{
                zIndex: 1,
              }}
              open={open}
              anchorEl={anchorRef.current}
              role={undefined}
              transition
              disablePortal
            >
              {({ TransitionProps, placement }) => (
                <Grow
                  {...TransitionProps}
                  style={{
                    transformOrigin:
                      placement === 'bottom' ? 'center top' : 'center bottom',
                  }}
                >
                  <Paper>
                    <ClickAwayListener onClickAway={handleCloseActions}>
                      <MenuList autoFocusItem>
                        {mainActions.map((button, index) => (
                          <MenuItem
                            key={`button${index}`}
                            selected={index === selectedIndex}
                            onClick={(event) =>
                              handleMenuItemClick(event, index)
                            }
                            children={button.children}
                          />
                        ))}
                      </MenuList>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
          </Stack>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Header;
